import { Box, Checkbox, Collapse, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper.ts";
import type {
  ChartOfAccountsItem,
  ConditionField,
  DimensionDictionary,
} from "../../../../../../../../../api/types/svTypes.ts";
import type { ChartOfAccountsContextItem } from "../contexts/ChartOfAccountsContext";
import { useChartOfAccountsContext } from "../contexts/ChartOfAccountsContext";
import { getFilterFieldSubItemUniqueId } from "../helpers/chartOfAccountsContextHelper";
import { useBookGrouping } from "../useBookGrouping.ts";
import FieldItemShowOnGridButton from "./FieldItemShowOnGridButton";
import FilterFieldBookGroupItem from "./FilterFieldBookGroupItem.tsx";
import FilterFieldChartOfAccountItem from "./FilterFieldChartOfAccountItem";

interface FilterItemProps {
  condition: ConditionField;
  chartOfAccountItems?: ChartOfAccountsItem[];
  dictionary: DimensionDictionary;
  parentNode: ChartOfAccountsContextItem | null;
  pl?: number;
}
export default function FilterFieldSubItem({
  condition,
  chartOfAccountItems,
  parentNode,
  dictionary,
  pl,
}: FilterItemProps) {
  const [conditionExpanded, setConditionExpanded] = useState(false);
  const [fieldIsHovered, setFieldIsHovered] = useState(false);
  const { root, appendChild, getNode, onSelect, scrollToAccountNo } = useChartOfAccountsContext();
  const { isMultiBook, groupByBook } = useBookGrouping();
  const id = getFilterFieldSubItemUniqueId(dictionary, parentNode?.identifier);

  const currentNode = useMemo(() => {
    if (!parentNode) return undefined;
    return getNode(id, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, id, root]);

  const { value, description } = useMemo(() => {
    return {
      value: dictionary[condition.meta.availableFields[0] || ""] as string,
      description: dictionary[condition.meta.availableFields[1] || ""] as string,
    };
  }, [dictionary, condition]);

  const chartOfAccounts = useMemo(() => {
    if (condition.meta.name === "AccountCategory") {
      return chartOfAccountItems?.filter((item) => item.accountCategory === value);
    }
    return undefined;
  }, [condition, chartOfAccountItems, value]);

  const isMultiBookCategory = isMultiBook && condition.meta.name === "AccountCategory";

  const groupedChartOfAccounts = useMemo(
    () => (isMultiBookCategory && chartOfAccounts ? groupByBook(chartOfAccounts, (item) => item.accountingBook) : null),
    [isMultiBookCategory, chartOfAccounts, groupByBook]
  );

  useEffect(() => {
    if (!parentNode) return;
    appendChild(id, parentNode, condition.meta.name !== "AccountCategory" ? value : undefined);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, id]);

  return (
    <>
      <Grid
        container
        sx={(theme) => ({
          justifyContent: "start",
          flexFlow: "row nowrap",
          bgcolor: currentNode?.checked ? makeTintedBackgroundFromColor(theme, theme.palette.primary.main) : "inherit",
          height: "28px",
          pl: pl ?? 4.5,
          pr: 1,
          ":hover": {
            cursor: "pointer",
            background: theme.palette.action.hover,
          },
        })}
        onMouseEnter={() => setFieldIsHovered(true)}
        onMouseLeave={() => setFieldIsHovered(false)}
      >
        <Grid sx={{ display: "flex", alignItems: "center", height: "28px" }}>
          {currentNode && (
            <Checkbox
              indeterminate={currentNode.indeterminate === true}
              checked={currentNode.checked === true}
              sx={{ width: "28px", height: "28px" }}
              onChange={(e) => {
                onSelect(currentNode.identifier, e.target.checked);
              }}
            />
          )}
          {chartOfAccounts && chartOfAccounts.length > 0 && (
            <IconButton sx={{ width: "28px", height: "28px" }} onClick={() => setConditionExpanded((prev) => !prev)}>
              {conditionExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </Grid>
        <Stack
          sx={{ flex: 1, position: "relative", width: "100%" }}
          onClick={() => currentNode && onSelect(currentNode.identifier, !currentNode.checked)}
        >
          <Box sx={{ position: "absolute", height: "100%", width: "100%", overflow: "hidden" }}>
            <Grid
              container
              sx={(theme) => ({
                height: "28px",
                color: theme.palette.text.primary,
                gap: 1,
                alignItems: "center",
                width: "100%",
              })}
            >
              <Grid
                sx={{
                  flex: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography variant="caption" sx={{ whiteSpace: "nowrap", px: 1 }}>
                  {value?.toString()}
                </Typography>
              </Grid>
              <Grid
                sx={{
                  flex: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography variant="caption" sx={{ whiteSpace: "nowrap", px: 1 }}>
                  {description}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Stack>
        <Grid>
          <FieldItemShowOnGridButton
            show={fieldIsHovered}
            onClick={() => currentNode && scrollToAccountNo(currentNode)}
          />
        </Grid>
      </Grid>
      <Collapse orientation="vertical" in={conditionExpanded}>
        {groupedChartOfAccounts
          ? Object.entries(groupedChartOfAccounts).map(([bookTitle, bookItems]) => (
              <FilterFieldBookGroupItem
                key={bookTitle}
                bookTitle={bookTitle}
                bookGroupId={`${id}-book-${bookTitle}`}
                parentNode={currentNode ?? null}
                pl={8}
              >
                {(bookGroupNode, childPl) =>
                  bookItems.map((item, i) => (
                    <FilterFieldChartOfAccountItem
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      key={`coa-${i}`}
                      index={i}
                      item={item}
                      parentNode={bookGroupNode}
                      pl={childPl}
                    />
                  ))
                }
              </FilterFieldBookGroupItem>
            ))
          : chartOfAccounts &&
            currentNode &&
            chartOfAccounts.map((item, i) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <FilterFieldChartOfAccountItem index={i} key={`coa-${i}`} item={item} parentNode={currentNode} />
            ))}
      </Collapse>
    </>
  );
}
